/**
 * Run an async mapper over ``items`` with at most ``concurrency`` in flight.
 * Preserves result order. Stops scheduling new work after the first rejection;
 * in-flight work may still finish.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  let failed = false;

  async function worker(): Promise<void> {
    while (true) {
      if (failed) return;
      const index = nextIndex++;
      if (index >= items.length) return;
      try {
        results[index] = await mapper(items[index]!, index);
      } catch (err) {
        failed = true;
        throw err;
      }
    }
  }

  const workerCount = Math.min(Math.max(1, concurrency), items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}
